import React from "react";
import { CircularProgress, Typography } from "@mui/material";
import useAppStore from "src/stores/appStore";
import { hasPermissionSelector } from "src/stores/selectors/appSelectors";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import useSnackbar from "@hyro-components/hooks/useSnackbar.jsx";
import HyroFeedbackMessage from "@hyro-components/HyroFeedbackMessage";
import HyroIconButton from "@hyro-components/hyro-icon-button/HyroIconButton";
import EyeClosedIcon from "src/icons/EyeClosedIcon";
import EyeOpenIcon from "src/icons/EyeOpenIcon";
import LockIcon from "src/icons/LockIcon";
import LockOpenIcon from "src/icons/LockOpenIcon";
import StatusChip from "../../../collections/StatusChip";
import { ICON_BUTTON_VARIANTS, ICON_BUTTON_SIZES } from "@hyro-components/hyro-icon-button/HyroIconButton.consts";
import useSecret from "./Secret.hook";
import { StyledTextField } from "../text-field/HyroTextField.styles";

const Secret = ({
  value,
  onChange,
  isReadOnly,
  isFieldReadOnlyFromSpec,
  status,
  label,
  error,
  fieldType,
  index,
  field: { key },
  path: [secretPath]
}) => {
  const hasPermission = useAppStore(hasPermissionSelector);
  const [
    isSnackbarOpen,
    snackbarMessage,
    addSnackbarMessage,
    exitSnackbar,
    closeSnackbar
  ] = useSnackbar();

  const {
    isValueLocked,
    localValue,
    isLoading,
    textFieldRef,
    handleTextFieldBlur,
    localOnChange,
    onButtonClick,
    handleKeyDown
  } = useSecret({ value, isReadOnly, onChange, addSnackbarMessage, label, secretPath, key });
  const hasRevealPermission = hasPermission(USER_PERMISSIONS.ABILITIES.REVEAL_INTEGRATION_SECRETS_ABILITY);
  const hasEditPermission = hasPermission(USER_PERMISSIONS.INTEGRATIONS_PAGE.WRITE);

  return (<div>
    <StyledTextField
      id={"locked-textfield" + label}
      fullWidth
      variant="outlined"
      isFieldReadOnlyFromSpec={isFieldReadOnlyFromSpec}
      readOnly={isReadOnly}
      type={!isValueLocked || localValue?.startsWith("**") || !hasRevealPermission ? "password" : "text"}
      value={localValue}
      placeholder={label}
      error={!!error}
      helperText={(error ? <Typography variant="caption">{error}</Typography> :
        <StatusChip status={status} />)}
      FormHelperTextProps={{ "data-testid": `${fieldType}-${index}-helper` }}
      onBlur={handleTextFieldBlur}
      onChange={({ target: { value } }) => localOnChange(value)}
      onKeyDown={handleKeyDown}
      inputProps={{ "data-testid": `${fieldType}-${index}`, "ref": textFieldRef }}
      InputProps={{
        endAdornment: (isLoading ? <CircularProgress size={16} sx={{ marginRight: 1 }} /> : value &&
          <HyroIconButton
            data-testid={`${fieldType}-${index}-button-${isValueLocked ? "visible" : "hidden"}`}
            disableRipple
            variant={ICON_BUTTON_VARIANTS.CLEAR}
            size={ICON_BUTTON_SIZES.XLARGE}
            active={isValueLocked}
            onClick={onButtonClick}
            disabled={isReadOnly || (!hasRevealPermission && !hasEditPermission)}
          >
            {
              hasRevealPermission ?
                (isValueLocked ? <EyeOpenIcon /> : <EyeClosedIcon />) :
                (isValueLocked ? <LockOpenIcon /> : <LockIcon />)
            }
          </HyroIconButton>),
        readOnly: isReadOnly || !isValueLocked,
        disabled: isReadOnly
      }}
    />
    <HyroFeedbackMessage
      message={snackbarMessage}
      severity="error"
      showFeedback={isSnackbarOpen}
      onExited={exitSnackbar}
      closeFeedback={closeSnackbar}
    />
  </div>);
};

export default Secret;
